import React from 'react';
import {TouchableOpacity, View, StyleSheet} from 'react-native';
import HTML, {RendererDeclaration, RendererDictionary, StylesDictionary} from 'react-native-render-html';
import {Image} from '../image';
import {Props} from './model';
import {useStyles, useStylesHtml} from '../../hooks/styles';
import {DeviceSize} from '../../theme/helpers';
import {Text} from '../text';
import {images} from '../../../assets/icons';
import {openExternalLink, stripEmptyProps} from '../../pipes';
import {useIsDesktop} from '../../hooks/useIsDesktop';
import {useSelector} from 'react-redux';
import {themeSelector} from '../../../redux/selectors/runtime/theme';
import {VisImpStatus} from '../../models/visuallyImpairedSettings';
import {ErrorBoundary} from '../errorBoundary';
import {getFontWeight} from '../../theme/default';

const desktopMainContentWidth = 874;
const iframeHeightToWidthRatio = 9 / 16;

const dataSetAttrs = {
  dataSet: {
    class: 'html',
  },
};

const linkRender = (): RendererDeclaration<any> => (htmlAttribs, children, convertedCSSStyles, passProps) => {
  const {parentWrapper, key, data} = passProps;
  const onPress = async () => {
    await openExternalLink(htmlAttribs.href as string);
  };
  if (parentWrapper === 'Text') {
    return (
      <Text {...{dataSet: {class: 'link-filled'}}} testID='a-renderer' onPress={onPress} key={key}>
        {children || data}
      </Text>
    );
  } else {
    return (
      <TouchableOpacity testID='b-renderer' onPress={onPress} key={key}>
        {children || data}
      </TouchableOpacity>
    );
  }
};

const createListRenderer = (
  type: 'ol' | 'ul',
  prefix: (index: number) => React.ReactNode,
  styles: StylesDictionary,
): RendererDeclaration<any> => {
  // @ts-ignore
  return (htmlAttribs, children, convertedCSSStyles, passProps) => {
    const {nodeIndex, key} = passProps;
    const childrenExtended = Array.isArray(children)
      ? children.map((child, index) => {
          return (
            <View key={`list-${nodeIndex}-${index}-${key}`} style={styles.li}>
              {prefix(index)}
              {child}
            </View>
          );
        })
      : children;
    return <View style={type === 'ol' ? styles.ol : styles.ul}>{childrenExtended}</View>;
  };
};

export default ({content, style, baseFontStyle, testID}: Props) => {
  const themeSettings = useSelector(themeSelector());
  const isVisuallyImpairedSettingActive = themeSettings.visuallyImpaired === VisImpStatus.Active;
  const [mainImageHeight, setMainImageHeight] = React.useState(0);
  const isDesktop = useIsDesktop();
  const styles = useStyles(({theme, addShadow}) => ({
    imgAlt: {
      borderRadius: 5,
      height: 100,
      paddingHorizontal: 10,
      backgroundColor: theme.color.loaderBgColor,
      borderStyle: 'dotted',
      borderWidth: 2,
      borderColor: theme.color.borderColor,
      justifyContent: 'center',
      alignItems: 'center',
      ...addShadow({
        color: theme.color.primaryBlackColor,
        opacity: 0.1,
        height: 2,
        radius: 17,
      }),
    },
    imgAltText: {
      color: theme.color.textColor,
    },
    ulPrefix: {
      width: 16,
      height: 24,
      color: theme.color.primaryColor,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 8,
    },
    olPrefix: {
      ...theme.fonts.body,
      color: theme.color.textColor,
      marginRight: 8,
      minWidth: 20,
      textAlign: 'right',
    },
  }));

  const tagsStyles: StylesDictionary = useStylesHtml(({theme, mediaOnly, deviceWidth}) =>
    stripEmptyProps({
      baseFont: {
        ...theme.fonts.body,
        color: theme.color.textColor,
      },
      a: {
        color: theme.color.primaryColor,
        textDecorationLine: 'none',
      },
      b: {
        marginTop: 30,
      },
      p: {
        ...theme.fonts.body,
        color: theme.color.textColor,
        marginBottom: 16,
      },
      strong: {
        fontWeight: getFontWeight('bold'),
        fontFamily: theme.fonts.fontFamily.bold,
        color: theme.color.textColor,
      },
      i: {
        fontStyle: 'italic',
        color: theme.color.textColor,
        marginVertical: 20,
      },
      li: {
        marginBottom: 10,
        color: theme.color.textColor,
        flexDirection: 'row',
        alignItems: 'flex-start',
      },
      ul: {
        marginBottom: 10,
      },
      ol: {
        marginBottom: 10,
        paddingLeft: 10,
      },
      blockquote: {
        paddingHorizontal: 10,
        paddingVertical: 10,
        color: theme.color.textColor,
      },
      time: {
        color: theme.color.labelText,
        ...mediaOnly(DeviceSize.desktop, theme.fonts.bodySmallLight, theme.fonts.label),
        lineHeight: 20,
      },
      h1: {
        ...theme.fonts.h1,
        color: theme.color.textColor,
        marginBottom: 16,
      },
      h2: {
        ...theme.fonts.h2,
        color: theme.color.textColor,
        marginBottom: 16,
      },
      h3: {
        ...theme.fonts.h3,
        color: theme.color.textColor,
        marginBottom: 16,
      },
      img: {
        width: '100%',
        ...mediaOnly(
          DeviceSize.desktop,
          {
            marginBottom: 24,
          },
          {
            marginBottom: 15,
          },
        ),
      },
      figure: {},
      figcaption: {},
      iframe: {
        maxWidth: '100%',
        marginBottom: 15,
        ...mediaOnly(
          DeviceSize.desktop,
          {height: desktopMainContentWidth * iframeHeightToWidthRatio},
          {height: deviceWidth * iframeHeightToWidthRatio},
        ),
      },
    }),
  );

  const classesStyles: StylesDictionary = useStylesHtml(({theme}) => ({
    'text-big': {
      fontSize: 24 * theme.fontSizeZoom,
      lineHeight: 32 * theme.fontSizeZoom,
    },
    'text-huge': {
      fontSize: 24 * theme.fontSizeZoom,
      lineHeight: 32 * theme.fontSizeZoom,
    },
    'text-tiny': {
      fontSize: 12 * theme.fontSizeZoom,
      lineHeight: 16 * theme.fontSizeZoom,
    },
    'text-small': {
      fontSize: 12 * theme.fontSizeZoom,
      lineHeight: 16 * theme.fontSizeZoom,
    },
    'text-medium-size': {
      fontSize: 20 * theme.fontSizeZoom,
      lineHeight: 28 * theme.fontSizeZoom,
    },
    'text-gray': {
      color: theme.color.labelText,
    },
    'text-main': {
      color: theme.color.primaryColor,
    },
    'text-red': {
      color: theme.html.textRed,
    },
    'text-normal': {
      fontSize: theme.fonts.body.fontSize,
      lineHeight: theme.fonts.body.lineHeight,
      fontWeight: getFontWeight('400'),
      fontFamily: theme.fonts.fontFamily.regular,
      letterSpacing: theme.fonts.body.letterSpacing ?? 0,
    },
    'text-medium': {
      fontSize: theme.fonts.body.fontSize,
      lineHeight: theme.fonts.body.lineHeight,
      fontWeight: getFontWeight('500'),
      fontFamily: theme.fonts.fontFamily.medium,
      letterSpacing: theme.fonts.body.letterSpacing ?? 0,
    },
    'text-center': {
      textAlign: 'center',
    },
    'text-underline': {
      textDecorationLine: 'underline',
    },
  }));

  if (!content) {
    return null;
  }

  const renderers: RendererDictionary<any> = {
    a: linkRender(),
    ul: createListRenderer(
      'ul',
      () => (
        <View style={styles.ulPrefix}>
          <images.Check width={16} height={16} color={styles.ulPrefix.color} />
        </View>
      ),
      tagsStyles,
    ),
    ol: createListRenderer('ol', (index) => <Text style={styles.olPrefix}>{index + 1}. </Text>, tagsStyles),
    // @ts-ignore
    img: (htmlAttribs) => {
      const {alt, src} = htmlAttribs;
      return src ? (
        <View style={tagsStyles.img}>
          <Image
            source={{uri: src as string}}
            width={isDesktop ? desktopMainContentWidth : undefined}
            height={mainImageHeight}
            style={{height: mainImageHeight}}
          />
        </View>
      ) : (
        <View style={[styles.imgAlt, tagsStyles.img]}>
          <Text style={styles.imgAltText}>{alt}</Text>
        </View>
      );
    },
    // @ts-ignore
    figure: (htmlAttribs, children) => <View style={tagsStyles.figure}>{children}</View>,
    // @ts-ignore
    figcaption: (htmlAttribs, children) => <Text style={tagsStyles.figcaption}>{children}</Text>,
    br: () => <Text />,
  };

  const setMainImageHeightByWidth = (width: number) => {
    setMainImageHeight((width / 3) * 2);
  };

  return (
    <View onLayout={(event) => setMainImageHeightByWidth(event.nativeEvent.layout.width)} style={style} testID={testID}>
      <ErrorBoundary>
        <HTML
          // @ts-ignore
          textSelectable
          baseFontStyle={{...StyleSheet.flatten(tagsStyles.baseFont), ...StyleSheet.flatten(baseFontStyle ?? {})}}
          html={content}
          tagsStyles={tagsStyles}
          classesStyles={classesStyles}
          ignoredStyles={isVisuallyImpairedSettingActive ? ['color', 'backgroundColor'] : undefined}
          renderers={renderers}
          {...dataSetAttrs}
        />
      </ErrorBoundary>
    </View>
  );
};
